import type { SelectOption } from 'types';
import { useState } from 'react';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { cn } from 'helpers/cn';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useOrganizations } from '@/state/useOrganizations';
import { useWebsites } from '@/state/useWebsites';
import { FOCUS_RING } from '../ConsolePanel';

/**
 * Which account and which website every screen is scoped to.
 *
 * These live in the header on a desktop and at the top of the navigation drawer on
 * a phone. They are their own module because of that: rendering them in two places
 * is a layout decision, and neither the header nor the sidebar should own the other.
 *
 * `stacked` is the drawer arrangement - full width, one per line, with a label -
 * because 228px cannot hold two dropdowns side by side and an unlabelled pair in a
 * drawer gives no clue what it scopes.
 */
type ScopePickersProps = { stacked?: boolean };

export const ScopePickers = ({ stacked }: ScopePickersProps) => (
  // `header-scope` anchors the platform tour's step; only one of the two copies is ever on screen.
  <div
    className={cn('flex min-w-0 items-center gap-2', stacked && 'flex-col items-stretch gap-2')}
    data-tour="header-scope"
  >
    {!stacked && <WebsiteFavicon />}
    <OrganizationPicker stacked={stacked} />
    <WebsitePicker stacked={stacked} />
  </div>
);

/**
 * The current website's own favicon, in a container matching the mark's.
 *
 * It answers "whose data am I looking at" faster than reading the picker does,
 * which matters on a shell where every screen is scoped to one website. It sits
 * beside the pickers rather than inside the website one so that it does not move
 * or resize as names of different lengths are selected.
 *
 * Only for https origins, and only the site's own /favicon.ico - same rule the
 * Websites list uses. No third-party favicon service: that would disclose which
 * sites a customer runs to whoever operates it.
 *
 * The plate stays light in both themes. A favicon is drawn for whatever background
 * its owner assumed, and that is almost always a light one, so a
 * black-on-transparent mark vanishes entirely against a dark surface. Giving
 * someone else's artwork its own plate is more honest than tinting it.
 */
export const WebsiteFavicon = () => {
  const { website } = useWebsites();
  const url = website?.url as string | undefined;
  if (!url?.startsWith('https')) return null;

  return (
    <span className="border-line bg-plate flex size-7 shrink-0 items-center justify-center rounded-[8px] border p-1">
      <Avatar className="size-full rounded-none">
        <AvatarImage src={`${url}/favicon.ico`} alt="" className="object-contain" />
        <AvatarFallback className="bg-transparent">
          {/* --dim is a mid grey in both themes, so it reads on the light plate. */}
          <Globe className="text-dim size-3.5" strokeWidth={1.5} />
        </AvatarFallback>
      </Avatar>
    </span>
  );
};

/**
 * border-input, not border-line: the pickers are outline-drawn controls and the
 * 10% hairline all but dissolved them into the header — these two answer "whose
 * data am I looking at", so they get the stronger outline the console gives its
 * other outline-only controls, a medium label, and a visible focus ring.
 */
const TRIGGER_CLASS =
  'border-input bg-field text-foreground flex h-8 items-center gap-2 rounded-lg border px-2.5 text-body font-medium ' +
  `transition-colors duration-150 hover:bg-hover ${FOCUS_RING}`;

type PickerProps = {
  options: SelectOption[] | null | undefined;
  selectedId?: number;
  label?: string;
  onSelect: (value: number) => void;
  dot?: boolean;
  placeholder: string;
  searchPlaceholder: string;
  stacked?: boolean;
};

/**
 * A searchable picker. The list is filtered rather than scrolled because an agency
 * can carry hundreds of accounts, which is why the selectors this replaces had a
 * search field too.
 *
 * Changing either selection while on an experiment or feature flag detail route sends you
 * back to its library - the id in the URL belongs to the account you just left.
 */
const Picker = (props: PickerProps) => {
  const { options, selectedId, label, onSelect, dot, placeholder, searchPlaceholder, stacked } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (next: number) => {
    setIsOpen(false);
    onSelect(next);
    if (/^\/experiments\/\d+/.test(location.pathname)) navigate('/experiments/library');
    else if (/^\/flags\/\d+/.test(location.pathname)) navigate('/flags/library');
  };

  return (
    <label className={cn('flex min-w-0', stacked ? 'flex-col gap-1' : 'contents')}>
      {stacked && <span className="micro-label">{placeholder}</span>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger className={cn(TRIGGER_CLASS, stacked && 'w-full justify-between')}>
          {dot && <span className="bg-ok size-1.5 shrink-0 rounded-pill" />}
          <span className={cn('truncate', !stacked && 'max-w-[180px]')}>{label ?? placeholder}</span>
          <ChevronDown className="text-dim size-3.5 shrink-0" strokeWidth={1.5} />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0">
          <Command>
            <CommandInput placeholder={searchPlaceholder} className="text-body" />
            <CommandList>
              <CommandEmpty className="supporting px-3 py-4">No matches.</CommandEmpty>
              <CommandGroup>
                {options?.map((option) => (
                  <CommandItem
                    key={option.value}
                    /* cmdk filters on this, so it must be the label — keying it by
                       id would make every search return nothing. */
                    value={option.label ?? String(option.value)}
                    onSelect={() => handleSelect(Number(option.value))}
                    className={cn(
                      'cursor-pointer text-body',
                      option.value === selectedId && 'text-foreground font-medium',
                    )}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-3.5 shrink-0',
                        option.value === selectedId ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </label>
  );
};

/**
 * Picking an account also moves the website selection into that account.
 *
 * `useWebsites` only auto-picks a website when none is set, so for an agency
 * switching accounts the previously selected website id stayed put - it belongs to
 * the account you just left, so it matches nothing in the new one and the website
 * picker falls back to its placeholder.
 */
const OrganizationPicker = ({ stacked }: { stacked?: boolean }) => {
  const { options, organizations, organization, setOrganization, setWebsite } = useOrganizations();

  const handleSelect = (id: number) => {
    setOrganization(id);
    const next = organizations?.find((account: { id: number }) => account.id === id);
    const firstWebsite = next?.websites?.[0]?.id;
    if (firstWebsite) setWebsite(firstWebsite);
  };

  return (
    <Picker
      options={options}
      selectedId={organization?.id}
      label={organization?.friendlyName || organization?.url}
      onSelect={handleSelect}
      placeholder="Account"
      searchPlaceholder="Search accounts"
      stacked={stacked}
    />
  );
};

const WebsitePicker = ({ stacked }: { stacked?: boolean }) => {
  const { options, website, setWebsite } = useWebsites();
  return (
    <Picker
      options={options}
      selectedId={website?.id}
      label={website?.friendlyName || website?.url}
      onSelect={setWebsite}
      placeholder="Website"
      searchPlaceholder="Search websites"
      stacked={stacked}
      dot
    />
  );
};
